import React, { useState, useMemo, useEffect } from 'react'
import { motion } from 'framer-motion'
import ProductsSearch from './ProductsSearch'
import CategoryShowcase from './CategoryShowcase'
import ProductCard from './ProductCard'
import Card1 from './ProductTabs/Card1'
import Card2 from './ProductTabs/Card2'
import Card3 from './ProductTabs/Card3'
import Card4 from './ProductTabs/Card4'
import Card5 from './ProductTabs/Card5'
import Card6 from './ProductTabs/Card6'
import Card7 from './ProductTabs/Card7'

// Import all product data
import ultraone from '../assets/ultra.png'
import def from '../assets/def.jpeg'
import machine from '../assets/machine.jpeg'
import denchair from '../assets/denchair.jpeg'
import pecker from '../assets/pecker.jpeg'
import zen from '../assets/zen.jpeg'
import retianers from '../assets/retainers.webp'
import gi from '../assets/gi.jpeg'
import hand from '../assets/hand.webp'
import reagents from '../assets/reagents.webp'
import flu from '../assets/flu.jpeg'
import test from '../assets/test.webp'
import edta from '../assets/edta.jpeg'
import clot from '../assets/CLOT.jpeg'
import cent from '../assets/cejt.jpeg'
import latext from '../assets/latext.webp'
import gnae from '../assets/gynae.jpeg'
import surgical from '../assets/surg.jpeg'
import syringes from '../assets/syringes.webp'
import cat from '../assets/cat.webp'
import pack from '../assets/pack.jpeg'
import fuji from '../assets/fuji.jpeg'
import thermal from '../assets/therm.png'
import ecg from '../assets/ecg.webp'
import ecgpaper from '../assets/ecg.jpeg'
// Dialysis Consumables images
import dialysisConcentrates from '../assets/Dialysis_Concentrates.webp'
import bicarbonateCartridges from '../assets/Bicarbonate_Cartridges.webp'
import bloodlines from '../assets/bloodlines.jpg'
import dialyzer from '../assets/Dialyzer.avif'
import dialysisMachine from '../assets/Dialysis_Machine.jpg'
import haemodialysisCatheter from '../assets/Haemodialysis_Catheter.avif'
import diasafeFilters from '../assets/Diasafe_Filters.jpg'

const categories = [
    { id: 0, name: 'All', component: Card1, products: [
        { id: 1, name: 'Portable Ultrasound Scanner', imgSrc: ultraone },
        { id: 2, name: 'Defibrillator', imgSrc: def },
        { id: 3, name: 'Patient Monitor', imgSrc: machine },
    ]},
    { id: 1, name: 'Dental', component: Card2, products: [
        { id: 4, name: 'Dental Chair', imgSrc: denchair },
        { id: 5, name: 'Woodpecker curing light', imgSrc: pecker },
        { id: 6, name: 'Zirconium / porcelain bridges & crowns', imgSrc: zen },
        { id: 7, name: 'Clear Aligner Retainers/ Dental Appliances', imgSrc: retianers },
        { id: 8, name: 'Dental composite material', imgSrc: gi },
        { id: 9, name: 'Dental Hand Instruments', imgSrc: hand },
    ]},
    { id: 2, name: 'Dialysis Consumables', component: Card7, products: [
        { id: 30, name: 'Dialysis Concentrates', imgSrc: dialysisConcentrates },
        { id: 31, name: 'Bicarbonate Cartridges', imgSrc: bicarbonateCartridges },
        { id: 32, name: 'Bloodlines', imgSrc: bloodlines },
        { id: 33, name: 'Dialyzers', imgSrc: dialyzer },
        { id: 34, name: 'Dialysis Machines', imgSrc: dialysisMachine },
        { id: 35, name: 'Haemodialysis Catheter', imgSrc: haemodialysisCatheter },
        { id: 37, name: 'Diasafe Filters', imgSrc: diasafeFilters },
    ]},
    { id: 3, name: 'Laboratory', component: Card3, products: [
        { id: 10, name: 'Reagents', imgSrc: reagents },
        { id: 11, name: 'Test kits & Analyzers', imgSrc: flu },
        { id: 12, name: 'Test Tubes', imgSrc: test },
        { id: 13, name: 'EDTA Tube', imgSrc: edta },
        { id: 14, name: 'VacuLab® Clot Activator Tube', imgSrc: clot },
        { id: 15, name: 'Medical Centrifuge', imgSrc: cent },
    ]},
    { id: 4, name: 'General Medical Consumables', component: Card4, products: [
        { id: 16, name: 'Latex Examination Gloves', imgSrc: latext },
        { id: 17, name: 'Gynaecological Gloves', imgSrc: gnae },
        { id: 18, name: 'Surgical Gloves', imgSrc: surgical },
        { id: 19, name: 'Syringes', imgSrc: syringes },
        { id: 20, name: 'Urinary Catheter & NG tubes', imgSrc: cat },
        { id: 21, name: 'Laparotomy & Caesarean Pack', imgSrc: pack },
    ]},
    { id: 5, name: 'X-Ray Imaging', component: Card5, products: [
        { id: 22, name: 'Fuji/Agfa Xray film', imgSrc: fuji },
        { id: 23, name: 'ECG/EKG machine', imgSrc: ecg },
        { id: 24, name: 'Thermal Paper', imgSrc: thermal },
        { id: 25, name: 'ECG Paper', imgSrc: ecgpaper },
    ]},
    { id: 6, name: 'Biomedical Engineering', component: Card6, products: [] },
]

export default function Products() {
    const [searchQuery, setSearchQuery] = useState('')
    const [activeCategory, setActiveCategory] = useState(0) // Default to "All"
    const [showAll, setShowAll] = useState(false)

    // Get all products
    const allProducts = useMemo(() => {
        return categories.flatMap(cat => cat.products)
    }, [])

    // Filter products
    const filteredProducts = useMemo(() => {
        let products = activeCategory === null || activeCategory === 0
            ? allProducts
            : categories[activeCategory]?.products || []

        // Search filter
        if (searchQuery) {
            products = products.filter(product =>
                product.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
        }

        return products
    }, [searchQuery, activeCategory, allProducts])

    // Reset showAll when category or search changes
    useEffect(() => {
        setShowAll(false)
    }, [activeCategory, searchQuery])

    // Products to display (6 at a time or all)
    const displayedProducts = showAll ? filteredProducts : filteredProducts.slice(0, 6)
    const hasMore = filteredProducts.length > 6

    return (
        <section id="products" className="py-20 bg-gradient-to-br from-white via-medical-blue-50/20 to-medical-teal-50/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Quality Medical Equipment & Solutions for Healthcare Facilities
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                        Explore quality medical equipment, tools, and service support designed for hospitals, clinics, and care facilities.
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-medical-blue-600 to-medical-teal-600 mx-auto mt-6 rounded-full"></div>
                </motion.div>

                {/* Search Bar */}
                <div className="mb-8">
                    <ProductsSearch
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                    />
                </div>

                {/* Category Showcase */}
                <div className="mb-12">
                    <CategoryShowcase
                        categories={categories}
                        onCategorySelect={setActiveCategory}
                        activeCategory={activeCategory}
                    />
                </div>

                <div>

                        {/* Biomedical Engineering Special Display */}
                        {activeCategory === 6 ? (
                            <Card6 />
                        ) : (
                            <>
                                {/* Results Header */}
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-8 p-4 sm:p-6 bg-gradient-to-r from-medical-blue-50 to-medical-teal-50 rounded-2xl border border-medical-blue-100">
                                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-medical-blue-700 to-medical-teal-700 bg-clip-text text-transparent">
                                        {activeCategory === 0 || activeCategory === null
                                            ? 'All Products'
                                            : categories[activeCategory]?.name + ' Products'
                                        }
                                    </h3>
                                    <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white rounded-full text-xs sm:text-sm font-semibold text-medical-blue-700 shadow-md border border-medical-blue-200 whitespace-nowrap">
                                        {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                                    </span>
                                </div>

                                {/* Product Grid */}
                                {filteredProducts.length > 0 ? (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {displayedProducts.map((product, index) => (
                                                <ProductCard key={product.id} product={product} index={index} />
                                            ))}
                                        </div>
                                        {hasMore && !showAll && (
                                            <div className="flex justify-center mt-8">
                                                <motion.button
                                                    onClick={() => setShowAll(true)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    className="px-8 py-3 bg-gradient-to-r from-medical-blue-600 to-medical-teal-600 text-white font-semibold rounded-lg hover:from-medical-blue-700 hover:to-medical-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                                                >
                                                    See More ({filteredProducts.length - 6} more)
                                                </motion.button>
                                            </div>
                                        )}
                                        {showAll && hasMore && (
                                            <div className="flex justify-center mt-8">
                                                <motion.button
                                                    onClick={() => setShowAll(false)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    className="px-8 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-200 shadow-md hover:shadow-lg"
                                                >
                                                    Show Less
                                                </motion.button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-12">
                                        <p className="text-gray-600 text-lg">No products found matching your criteria.</p>
                                        <button
                                            onClick={() => {
                                                setSearchQuery('')
                                                setActiveCategory(0)
                                            }}
                                            className="mt-4 px-6 py-2 bg-medical-blue-600 text-white font-semibold rounded-lg hover:bg-medical-blue-700 transition-all"
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* CTA Section */}
                        <div className="mt-12 bg-gradient-to-r from-medical-blue-600 to-medical-teal-600 rounded-2xl p-8 md:p-12 text-white text-center">
                            <h3 className="text-3xl font-bold mb-4">Need Bulk Orders or Custom Solutions?</h3>
                            <p className="text-xl text-white/90 mb-6 max-w-2xl mx-auto">
                                Contact our sales team for personalized quotes and volume discounts
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href="tel:+233303955917"
                                    className="px-8 py-3 bg-white text-medical-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg"
                                >
                                    Call Sales Team
                                </a>
                                <a
                                    href="mailto:info@alivanymedicalsupplies.com"
                                    className="px-8 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all"
                                >
                                    Email Us
                                </a>
                            </div>
                        </div>
                </div>
            </div>
        </section>
    )
}
